import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["base", "table", "titleField", "contentFields", "languageField", "error"]
  static values = {
    tablesUrl: String,
    bases: Array,
    initialTables: Array,
    initialBaseId: String,
    initialTableId: String,
    initialTitleField: String,
    initialContentFields: Array,
    initialLanguageField: String,
  }

  async connect() {
    this.tablesCache = new Map()
    this.setOptions(this.baseTarget, this.basesValue, { blank: "Select a base..." })
    if (this.initialBaseIdValue) {
      this.setValue(this.baseTarget, this.initialBaseIdValue)
      if (this.initialTablesValue && this.initialTablesValue.length) {
        this.tablesCache.set(this.initialBaseIdValue, this.initialTablesValue)
      }
      await this.loadTables()
      if (this.initialTableIdValue) {
        this.setValue(this.tableTarget, this.initialTableIdValue)
        this.populateFieldSelects()
      }
    }
  }

  async onBaseChange() {
    this.clearDownstream()
    await this.loadTables()
  }

  onTableChange() {
    this.populateFieldSelects()
  }

  async loadTables() {
    const baseId = this.baseTarget.value
    if (!baseId) return
    try {
      let tables = this.tablesCache.get(baseId)
      if (!tables) {
        const url = `${this.tablesUrlValue}?base_id=${encodeURIComponent(baseId)}`
        const res = await fetch(url, { headers: { Accept: "application/json" } })
        if (!res.ok) throw new Error(`Failed to load tables (${res.status})`)
        const data = await res.json()
        tables = data.tables
        this.tablesCache.set(baseId, tables)
      }
      this.currentTables = tables
      this.setOptions(this.tableTarget, tables, { blank: "Select a table..." })
    } catch (e) {
      this.showError(e.message)
    }
  }

  populateFieldSelects() {
    const table = this.currentTables?.find(t => t.id === this.tableTarget.value)
    const fields = (table?.fields || []).map(f => ({ id: f.name, name: f.name }))
    this.setOptions(this.titleFieldTarget, fields, { blank: "Select a field..." })
    this.setOptions(this.languageFieldTarget, fields, { blank: "None (English only)" })
    this.setMultiOptions(this.contentFieldsTarget, fields, this.initialContentFieldsValue || [])
    if (this.initialTitleFieldValue && fields.some(f => f.id === this.initialTitleFieldValue)) {
      this.setValue(this.titleFieldTarget, this.initialTitleFieldValue)
    }
    if (this.initialLanguageFieldValue && fields.some(f => f.id === this.initialLanguageFieldValue)) {
      this.setValue(this.languageFieldTarget, this.initialLanguageFieldValue)
    }
  }

  clearDownstream() {
    this.currentTables = []
    this.initialTableIdValue = ""
    this.initialTitleFieldValue = ""
    this.initialContentFieldsValue = []
    this.initialLanguageFieldValue = ""
    this.setOptions(this.tableTarget, [], { blank: "Select a table..." })
    this.setOptions(this.titleFieldTarget, [], { blank: "Select a field..." })
    this.setOptions(this.languageFieldTarget, [], { blank: "None (English only)" })
    this.setMultiOptions(this.contentFieldsTarget, [], [])
  }

  setOptions(select, items, { blank = null } = {}) {
    const ts = select.tomselect
    if (ts) {
      ts.clear(true)
      ts.clearOptions()
      if (blank) ts.addOption({ value: "", text: blank })
      for (const item of items) ts.addOption({ value: item.id, text: item.name })
      ts.refreshOptions(false)
      return
    }
    select.innerHTML = ""
    if (blank) {
      const opt = document.createElement("option")
      opt.value = ""
      opt.textContent = blank
      select.appendChild(opt)
    }
    for (const item of items) {
      const opt = document.createElement("option")
      opt.value = item.id
      opt.textContent = item.name
      select.appendChild(opt)
    }
  }

  setMultiOptions(select, items, selectedValues) {
    const ts = select.tomselect
    if (ts) {
      ts.clear(true)
      ts.clearOptions()
      for (const item of items) ts.addOption({ value: item.id, text: item.name })
      ts.refreshOptions(false)
      if (selectedValues.length) ts.setValue(selectedValues, true)
      return
    }
    select.innerHTML = ""
    for (const item of items) {
      const opt = document.createElement("option")
      opt.value = item.id
      opt.textContent = item.name
      if (selectedValues.includes(item.id)) opt.selected = true
      select.appendChild(opt)
    }
  }

  setValue(select, value) {
    const ts = select.tomselect
    if (ts) {
      ts.setValue(value, true)
    } else {
      select.value = value
    }
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove("hidden")
    }
  }
}
